import React, { useState, useEffect, useRef, useCallback } from "react";
import {
  Box,
  Button,
  Grid,
  TextField,
  FormControl,
  FormLabel,
  Select,
  MenuItem,
  Typography,
  Switch,
  FormControlLabel,
  Paper,
  Divider,
  InputLabel,
  IconButton,
  Tooltip,
  InputAdornment,
  CircularProgress,
  Avatar,
} from "@mui/material";
import Autocomplete from "@mui/material/Autocomplete";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import { Search, Phone, Badge } from "@mui/icons-material";
import { useTranslation } from "react-i18next";
import { useAuth } from "../../../../context";
import useFetchObjects from "../../../../api/useFetchObjects";
import useAdd from "../../../../api/useAdd";
import { useNavigate } from "react-router-dom";
import { getLocalDateString } from "../../../../utils";

// Debounce function
function debounce(func, delay) {
  let timeout;
  return function (...args) {
    const context = this;
    clearTimeout(timeout);
    timeout = setTimeout(() => func.apply(context, args), delay);
  };
}

const UltrasoundReferral = () => {
  const { t } = useTranslation();
  const auth = useAuth();
  const token = auth?.user?.token;
  const navigate = useNavigate();

  const today = getLocalDateString();
  const [ticketSearch, setTicketSearch] = useState("");
  const selectedTicketSnapshot = useRef(null);

  const { data: ultrasoundTypesData } = useFetchObjects(
    ["ultrasound-types"],
    "ultrasound-types/",
    token
  );
  const effectiveTicketSearch = selectedTicketSnapshot.current ? "" : ticketSearch;
  const { data: ticketsData, isLoading: isLoadingTickets } = useFetchObjects(
    ["tickets-ultrasound-reception", today, effectiveTicketSearch],
    `tickets/?visit_date=${today}&search=${encodeURIComponent(effectiveTicketSearch)}&page_size=200`,
    token,
    { enabled: !!token }
  );

  const { handleAdd, resetForm } = useAdd("ultrasounds", token);

  const ultrasoundTypes = ultrasoundTypesData || [];
  const tickets = ticketsData?.results ?? (Array.isArray(ticketsData) ? ticketsData : []);

  const [ultrasoundDetails, setUltrasoundDetails] = useState({
    ultrasoundType: "",
    date: getLocalDateString(),
    isUrgent: false,
    totalAmount: "",
    ticket: null,
    doctor: auth?.user?.user?.id,
  });

  const [errors, setErrors] = useState({});

  useEffect(() => {
    if (resetForm) {
      setUltrasoundDetails({
        ultrasoundType: "",
        date: getLocalDateString(),
        isUrgent: false,
        totalAmount: "",
        ticket: null,
        doctor: auth?.user?.user?.id,
      });
      setErrors({});
      selectedTicketSnapshot.current = null;
    }
  }, [resetForm, auth?.user?.user?.id]);

  const handleChange = (event) => {
    const { name, value, checked, type } = event.target;
    setUltrasoundDetails((prev) => ({
      ...prev,
      [name]: type === "checkbox" ? checked : value,
    }));

    if (name === "ultrasoundType") {
      const selectedType = ultrasoundTypes.find(
        (type) => type.id.toString() === value
      );
      setUltrasoundDetails((prev) => ({
        ...prev,
        totalAmount: selectedType ? selectedType.cost.toString() : "",
      }));
    }

    setErrors((prev) => ({
      ...prev,
      [name]: "",
    }));
  };

  const validateForm = () => {
    let tempErrors = {};
    if (!ultrasoundDetails.ticket) {
      tempErrors.ticket = t("doctor.selectTicketRequired");
    }
    if (!ultrasoundDetails.ultrasoundType) {
      tempErrors.ultrasoundType = t("doctor.ultTypeRequired");
    }
    if (!ultrasoundDetails.date) {
      tempErrors.date = t("doctor.testDateRequired");
    }
    if (!ultrasoundDetails.totalAmount) {
      tempErrors.totalAmount = t("doctor.totalAmountRequired");
    }
    setErrors(tempErrors);
    return Object.keys(tempErrors).length === 0;
  };

  const handleSubmit = () => {
    if (validateForm()) {
      const patientId = typeof ultrasoundDetails.ticket.patient === "object" ? ultrasoundDetails.ticket.patient?.id : ultrasoundDetails.ticket.patient;
      const payload = {
        patient: patientId,
        ticket: ultrasoundDetails.ticket.id,
        doctor: ultrasoundDetails.doctor,
        ultrasound_type: ultrasoundDetails.ultrasoundType,
        date: ultrasoundDetails.date,
        is_urgent: ultrasoundDetails.isUrgent,
        total_amount: ultrasoundDetails.totalAmount,
      };
      handleAdd(payload);
    }
  };

  return (
    <Paper
      elevation={3}
      sx={{
        p: 4,
        margin: "auto",
        width: "100%",
        borderRadius: "16px",
        backgroundColor: "background.default",
        boxShadow: "0px 6px 18px rgba(0,0,0,0.12)",
      }}
    >
      {/* Go Back Icon and Title */}
      <Box display="flex" alignItems="center" mb={4}>
        <Tooltip title={t("reception.goBack")} arrow>
          <IconButton
            onClick={() => navigate(-1)}
            aria-label={t("reception.goBack")}
            sx={{ mr: 2 }}
          >
            <ArrowBackIcon size="large" />
          </IconButton>
        </Tooltip>
      </Box>

      <Typography
        variant="h4"
        textAlign="center"
        fontWeight="bold"
        sx={{ mb: 3 }}
        color="primary"
      >
        {t("doctor.ultReferralForm")}
      </Typography>

      <Divider sx={{ mb: 4 }} />

      <Grid container spacing={3}>
        <Grid item xs={12} sm={6}>
          <Autocomplete
            options={tickets}
            getOptionLabel={(option) =>
              option ? `${option.ticket_number || option.id}${option.patient_name_display ? ` - ${option.patient_name_display}` : ""}` : ""
            }
            value={ultrasoundDetails.ticket}
            inputValue={ticketSearch}
            onInputChange={(e, value) => {
              setTicketSearch(value);
              if (!value) selectedTicketSnapshot.current = null;
            }}
            onChange={(event, newValue) => {
              selectedTicketSnapshot.current = newValue ? { id: newValue.id } : null;
              setUltrasoundDetails((prev) => ({ ...prev, ticket: newValue }));
              setErrors((prev) => ({ ...prev, ticket: "" }));
            }}
            loading={isLoadingTickets}
            renderInput={(params) => (
              <TextField
                {...params}
                label={t("print.ticketNumber")}
                placeholder={t("doctor.selectTicketPlaceholder")}
                variant="outlined"
                fullWidth
                error={!!errors.ticket}
                helperText={errors.ticket}
                InputProps={{
                  ...params.InputProps,
                  startAdornment: (
                    <InputAdornment position="start">
                      <Search />
                    </InputAdornment>
                  ),
                  endAdornment: (
                    <React.Fragment>
                      {isLoadingTickets ? (
                        <CircularProgress color="inherit" size={20} />
                      ) : null}
                      {params.InputProps.endAdornment}
                    </React.Fragment>
                  ),
                }}
              />
            )}
            isOptionEqualToValue={(option, value) => option?.id === value?.id}
          />
        </Grid>
        {ultrasoundDetails.ticket && (
          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth
              label={t("laboratory.patientName")}
              value={ultrasoundDetails.ticket.patient_name_display ?? ""}
              variant="outlined"
              InputProps={{ readOnly: true }}
            />
          </Grid>
        )}

        <Grid item xs={12} sm={6}>
          <FormControl fullWidth>
            <InputLabel>{t("doctor.ultrasoundType")}</InputLabel>
            <Select
              name="ultrasoundType"
              value={ultrasoundDetails.ultrasoundType}
              onChange={handleChange}
              displayEmpty
              error={!!errors.ultrasoundType}
            >
              {ultrasoundTypes.map((type) => (
                <MenuItem key={type.id} value={type.id.toString()}>
                  {type.name}
                </MenuItem>
              ))}
            </Select>
            {errors.ultrasoundType && (
              <Typography color="error" variant="caption">
                {errors.ultrasoundType}
              </Typography>
            )}
          </FormControl>
        </Grid>

        <Grid item xs={12} sm={6}>
          <TextField
            name="date"
            label={t("doctor.testDate")}
            type="date"
            fullWidth
            value={ultrasoundDetails.date}
            onChange={handleChange}
            InputLabelProps={{ shrink: true }}
            error={!!errors.date}
            helperText={errors.date}
          />
        </Grid>

        <Grid item xs={12} sm={6}>
          <TextField
            name="totalAmount"
            label={t("doctor.totalAmount")}
            type="number"
            fullWidth
            value={ultrasoundDetails.totalAmount}
            onChange={handleChange}
            error={!!errors.totalAmount}
            helperText={errors.totalAmount}
          />
        </Grid>

        <Grid item xs={12}>
          <FormControlLabel
            control={
              <Switch
                checked={ultrasoundDetails.isUrgent}
                onChange={handleChange}
                name="isUrgent"
              />
            }
            label={t("doctor.urgent")}
          />
        </Grid>

        <Grid item xs={12}>
          <Button
            variant="contained"
            color="primary"
            fullWidth
            onClick={handleSubmit}
          >
            {t("doctor.submit")}
          </Button>
        </Grid>
      </Grid>
    </Paper>
  );
};

export default UltrasoundReferral;
